import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

import { Dropdown, ErrorMessage, Heading2 } from "widgets";
import EditPackageDetails from "components/EditPackageDetails/EditPackageDetails";
import { usePackageVersion } from "store/backend/apiHooks";

import styles from "./SearchedPackageDetails.module.scss";
import Loader from "shared/Loader/Loader";

const DisplayDetails = ({ title, value }) => (
  <div className={styles.displayDetails}>
    <span className={styles.title}>{title}</span>
    <span className={styles.value}>{value || "N/A"}</span>
  </div>
);

const getVersionOptions = (versions) => {
  return (
    versions?.map((version) => ({
      label: `V${version.version}`,
      value: version.package_id,
    })) || []
  );
};

const SearchedPackageDetails = (props) => {
  const { packageDetails, onVersionChange, versions,isActive } = props;
  const [selectedVersion, setSelectedVersion] = useState();
  const [packageVersionDetails, setPackageVersionDetails] = useState(null);

  const activePackage = packageDetails || {};

  const featureOfferings = activePackage.feature_offerings || {};
  const versionOptions = getVersionOptions(versions);

  const { data: versionDetails, isLoading,isError, error } = usePackageVersion(selectedVersion);

  useEffect(() => {
    if (versionDetails) {
      setPackageVersionDetails(versionDetails);
    }
  }, [versionDetails]);

  const handleVersionChange = (event) => {
    const selectedPackageId = event.target.value;
    setSelectedVersion(selectedPackageId);

    if (onVersionChange) {
      onVersionChange(selectedPackageId);
    }
  };

  if (isLoading) {
    return <Loader/>;
  }

  if (isError) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  const packageToDisplay = packageVersionDetails || activePackage;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.flexContainer}>
        <Heading2 heading="Package Details" />
        <div className={styles.packageDetailsInnerContainer}>
          <div className={styles.defaultValues}>
            <DisplayDetails
              title="Internal Package Name"
              value={packageToDisplay.display_name}
            />
            {isActive ? <span className={styles.activeStatus}>Active For Sale</span>: 
            <span className={styles.notActiveStatus}>Inactive for Sale</span> }
          </div>
          <div className={styles.versionDisplay}>
            <div className={styles.dropdown}>
              <span className={styles.title}>Select Version ID</span>
              <Dropdown
                label="Existing Version IDs"
                options={versionOptions}
                value={selectedVersion}
                onChange={handleVersionChange}
              />
            </div>
            <DisplayDetails
              title="Version Id"
              value={`V${packageToDisplay.version}`}
            />
          </div>
          <DisplayDetails
            title="Package Description"
            value={packageToDisplay.description}
          />
          <EditPackageDetails
            activePackage={packageToDisplay}
          />
        </div>
      </div>
      <div className={styles.flexContainer}>
        <Heading2 heading="Package Feature Offerings" />
        <div className={styles.offeringInnerContainer}>
          {featureOfferings.products?.map((product, index) => (
            <div className={styles.productOfferings} key={index}>
              <DisplayDetails
                title="Offering Name"
                value={product.offering_name}
              />
              <div className={styles.defaultValues}>
                <DisplayDetails title="Quantity" value={product.quantity} />
              </div>
              <DisplayDetails title="Description" value={product.description} />
            </div>
          ))}
          {featureOfferings.services?.map((service, index) => (
            <div className={styles.serviceOfferings} key={index}>
              <DisplayDetails
                title="Offering Name"
                value={service.offering?.name}
              />
              <DisplayDetails title="Features" value={service.feature?.name} />
              <div className={styles.defaultValues}>
                <DisplayDetails
                  title="Start Usage Date"
                  value={service.start_and_end_usage_date?.start_usage_date}
                />
                <DisplayDetails
                  title="End Usage Date"
                  value={service.start_and_end_usage_date?.end_usage_date}
                />
                <DisplayDetails
                  title="Quantity"
                  value={service.quantity || "N/A"}
                />
                <DisplayDetails
                  title="Duration"
                  value={
                    service.duration
                      ? `${service.duration} ${service.duration_type?.name || ""}`
                      : "N/A"
                  }
                />
              </div>
              <DisplayDetails title="Description" value={service.description} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchedPackageDetails;
